import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Copy, Mail, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loading } from "@/components/Loading";
import { Notice } from "@/components/Notice";
import { StatusPill, type StatusTone } from "@/components/StatusPill";
import { useConfirm } from "@/components/ConfirmDialog";
import { toastApiError } from "@/lib/toast";
import { url } from "@/lib/basePath";
import { isEmail } from "@/utils/validation";
import type { V2Bag, V2Value } from "@/features/applications/api/applications.api";
import {
  PRICING_FIELDS, PRICING_DEPENDENTS, PricingConfigurator, ReadOnlyContext, ValidationContext,
  isEmptyVal, overMaxLength, validateField,
} from "@/features/applications/components/v2";
import { onboarding, pricedLinks, type PricedLink } from "@/features/onboarding/api/onboarding.api";

const LABEL_MAX = 100;
const DAY_MS = 24 * 60 * 60 * 1000;
const SOON_DAYS = 3;
const PRICING_BY_KEY = new Map(PRICING_FIELDS.map((d) => [d.key, d]));

type Editing = { link: PricedLink | null; label: string; bag: V2Bag };

function withoutHidden(bag: V2Bag): V2Bag {
  let out = bag;
  for (const d of PRICING_FIELDS) {
    if (d.showWhen && !d.showWhen(out) && !isEmptyVal(out[d.key] ?? null)) out = { ...out, [d.key]: null };
  }
  return out;
}

function pricingIssues(bag: V2Bag): { missing: Set<string>; errors: Map<string, string> } {
  const missing = new Set<string>();
  const errors = new Map<string, string>();
  for (const d of PRICING_FIELDS) {
    if (d.showWhen && !d.showWhen(bag)) continue;
    const val = bag[d.key] ?? null;
    const required = d.required || (d.requiredWhen ? d.requiredWhen(bag) : false);
    if (isEmptyVal(val)) {
      if (required) missing.add(d.key);
      continue;
    }
    if (d.options && typeof val === "string" && !d.options.some((o) => (typeof o === "string" ? o : o.value) === val)) {
      errors.set(d.key, "Pick a value from the list.");
      continue;
    }
    const msg = validateField(d, val, bag) ?? (overMaxLength(d.maxLength, val) ? `${d.label} is too long.` : null);
    if (msg) errors.set(d.key, msg);
  }
  return { missing, errors };
}

function pricingOf(bag: V2Bag): Record<string, V2Value> {
  const out: Record<string, V2Value> = {};
  for (const d of PRICING_FIELDS) {
    const v = bag[d.key] ?? null;
    if (!isEmptyVal(v)) out[d.key] = v;
  }
  return out;
}

function summary(link: PricedLink): string {
  const p = link.pricing;
  const parts: string[] = [];
  if (typeof p.pricingProgram === "string") {
    let main = p.pricingProgram;
    if (p.pricingRatePct != null) main += ` ${p.pricingRatePct}%`;
    if (p.pricingPerTxnFee != null) main += ` + $${Number(p.pricingPerTxnFee).toFixed(2)}`;
    parts.push(main);
  }
  if (p.onlineOrdering === true) parts.push("Online ordering");
  if (p.acceptAch === true) parts.push("ACH");
  if (p.pinDebitEnabled === true) parts.push("PIN debit");
  if (p.sensaInvoices === true) parts.push("SensaInvoices");
  return parts.join(" · ");
}

function localDate(value: string | null | undefined): string {
  if (!value) return "";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function pill(link: PricedLink): { value: string; tone: StatusTone } {
  if (link.state === "Used") return { value: "Applied", tone: "success" };
  if (link.state === "Expired") return { value: "Expired", tone: "muted" };
  if (link.state === "Cancelled") return { value: "Cancelled", tone: "muted" };
  const expires = new Date(link.expiresAt);
  if (expires.getTime() <= Date.now()) return { value: "Expired", tone: "muted" };
  const startOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((startOf(expires) - startOf(new Date())) / DAY_MS);
  if (days <= SOON_DAYS) return { value: days <= 0 ? "Expires today" : days === 1 ? "Expires tomorrow" : `Expires in ${days} days`, tone: "warning" };
  return { value: "Waiting on merchant", tone: "blue" };
}

export default function PricedLinksCard({ agentNumber }: { agentNumber: number }) {
  const confirm = useConfirm();
  const [links, setLinks] = useState<PricedLink[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [missing, setMissing] = useState<Set<string>>(new Set());
  const [errors, setErrors] = useState<Map<string, string>>(new Map());
  const [saving, setSaving] = useState(false);
  const [emailing, setEmailing] = useState<string | null>(null);
  const [emailTo, setEmailTo] = useState("");
  const [emailName, setEmailName] = useState("");
  const [sending, setSending] = useState(false);
  const [justCreated, setJustCreated] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState<string | null>(null);
  const currentAgent = useRef(agentNumber);
  currentAgent.current = agentNumber;

  const load = useCallback(async () => {
    const forAgent = agentNumber;
    try {
      const list = await pricedLinks.list(forAgent);
      if (currentAgent.current !== forAgent) return;
      setLinks(list);
      setLoadError(null);
    } catch (e) {
      if (currentAgent.current !== forAgent) return;
      setLoadError(e instanceof Error ? e.message : "Couldn't load your pre-priced links.");
    }
  }, [agentNumber]);

  useEffect(() => {
    setLinks(null);
    setLoadError(null);
    setEditing(null);
    setJustCreated(null);
    setEmailing(null);
    setEmailTo("");
    setEmailName("");
    void load();
  }, [load]);

  useEffect(() => {
    setEmailTo("");
    setEmailName("");
  }, [emailing]);

  const markFilled = useCallback((key: string) => {
    setMissing((prev) => {
      if (!prev.has(key)) return prev;
      const n = new Set(prev);
      n.delete(key);
      return n;
    });
    setErrors((prev) => {
      if (!prev.has(key)) return prev;
      const n = new Map(prev);
      n.delete(key);
      return n;
    });
  }, []);
  const markMissing = useCallback((key: string) => {
    setMissing((prev) => (prev.has(key) ? prev : new Set(prev).add(key)));
  }, []);
  const markError = useCallback((key: string, message: string) => {
    setErrors((prev) => (prev.get(key) === message ? prev : new Map(prev).set(key, message)));
  }, []);
  const validation = useMemo(
    () => ({ missing, errors, markFilled, markMissing, markError, prefix: "" }),
    [missing, errors, markFilled, markMissing, markError],
  );

  function startEdit(link: PricedLink | null, copyPricing?: PricedLink) {
    const source = link ?? copyPricing;
    setMissing(new Set());
    setErrors(new Map());
    setEditing({
      link,
      label: source?.label ?? "",
      bag: source ? ({ ...source.pricing } as V2Bag) : {},
    });
  }

  function change(key: string, value: V2Value) {
    setEditing((prev) => {
      if (!prev) return prev;
      const next = withoutHidden({ ...prev.bag, [key]: value });
      for (const dep of PRICING_DEPENDENTS[key] ?? []) {
        const def = PRICING_BY_KEY.get(dep);
        const val = next[dep] ?? null;
        if (!def || (def.showWhen && !def.showWhen(next)) || isEmptyVal(val)) { markFilled(dep); continue; }
        const msg = validateField(def, val, next);
        if (msg) markError(dep, msg); else markFilled(dep);
      }
      return { ...prev, bag: next };
    });
  }

  async function save() {
    if (!editing) return;
    const issues = pricingIssues(editing.bag);
    if (issues.missing.size > 0 || issues.errors.size > 0) {
      setMissing(issues.missing);
      setErrors(issues.errors);
      toast.error("Fix the highlighted pricing fields first.");
      return;
    }
    setSaving(true);
    try {
      const body = { agentNumber, label: editing.label.trim() || null, pricing: pricingOf(editing.bag) };
      const saved = editing.link
        ? await pricedLinks.update(editing.link.id, body)
        : await pricedLinks.create(body);
      setEditing(null);
      setJustCreated(editing.link ? null : saved.id);
      toast.success(editing.link ? "Pricing updated. The same link still works." : "Link created. Copy it or email it to your merchant.");
      await load();
    } catch (e) {
      toastApiError(e, "Couldn't save the link.");
    } finally {
      setSaving(false);
    }
  }

  async function copy(link: PricedLink) {
    try {
      await navigator.clipboard.writeText(url(link.path));
      toast.success("Link copied.");
    } catch {
      toast.error("Couldn't reach the clipboard. Select the link text and copy it manually.");
    }
  }

  async function cancel(link: PricedLink) {
    const ok = await confirm({
      title: "Cancel this link?",
      description: "The merchant won't be able to use it any more. You can create a new one with the same pricing.",
      confirmLabel: "Cancel link",
      destructive: true,
    });
    if (!ok) return;
    setCancelling(link.id);
    try {
      await pricedLinks.cancel(link.id);
      toast.success("Link cancelled.");
      await load();
    } catch (e) {
      toastApiError(e, "Couldn't cancel the link.");
    } finally {
      setCancelling(null);
    }
  }

  async function sendEmail(link: PricedLink) {
    const to = emailTo.trim();
    if (!isEmail(to)) {
      toast.error("Enter a valid email address.");
      return;
    }
    setSending(true);
    try {
      await onboarding.sendLink(agentNumber, to, emailName.trim() || null, "APPLY", link.code);
      toast.success(`Sent to ${to}.`);
      setEmailing(null);
      setEmailTo("");
      setEmailName("");
    } catch (e) {
      toastApiError(e, "Couldn't send the email.");
    } finally {
      setSending(false);
    }
  }

  return (
    <Card>
      <CardHeader className="border-b py-3 flex flex-col gap-3 space-y-0 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="space-y-1">
          <CardTitle className="text-base font-semibold text-slate-900">Pre-priced links</CardTitle>
          <CardDescription>
            Set pricing now and send a link for one merchant. You won't need to configure pricing after they apply.
          </CardDescription>
        </div>
        {!editing && (
          <Button type="button" size="sm" onClick={() => startEdit(null)} className="shrink-0">
            <Plus />
            Create pre-priced link
          </Button>
        )}
      </CardHeader>
      <CardContent className="pt-6 space-y-4">
        {editing && (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="pricedLinkLabel">
                Label <span className="text-muted-foreground font-normal">(only you see this)</span>
              </Label>
              <Input
                id="pricedLinkLabel"
                value={editing.label}
                maxLength={LABEL_MAX}
                placeholder="e.g. Pho house on 5th"
                onChange={(e) => setEditing((p) => (p ? { ...p, label: e.target.value } : p))}
              />
            </div>
            <ValidationContext.Provider value={validation}>
              <ReadOnlyContext.Provider value={false}>
                <PricingConfigurator bag={editing.bag} onChange={change} />
              </ReadOnlyContext.Provider>
            </ValidationContext.Provider>
            <div className="flex flex-wrap justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setEditing(null)} disabled={saving}>
                Cancel
              </Button>
              <Button type="button" onClick={save} disabled={saving}>
                {saving ? "Saving…" : editing.link ? "Save pricing" : "Create link"}
              </Button>
            </div>
          </div>
        )}

        {loadError && <Notice tone="error">{loadError}</Notice>}
        {links === null && !loadError && <Loading>Loading…</Loading>}
        {links !== null && links.length === 0 && !editing && (
          <p className="text-sm text-muted-foreground">No pre-priced links yet.</p>
        )}

        {links !== null && links.length > 0 && (
          <ul className="divide-y rounded-md border">
            {links.map((link) => {
              const status = pill(link);
              const open = link.state === "Open" && new Date(link.expiresAt).getTime() > Date.now();
              if (link.state === "Used") {
                const name = link.applicationDba || link.label || "Pre-priced link";
                const row = (
                  <>
                    <span className="min-w-0 flex-1 truncate">
                      <span className="font-medium text-slate-800">{name}</span>
                      {summary(link) && <span className="text-slate-500"> · {summary(link)}</span>}
                    </span>
                    <span className="flex shrink-0 items-center gap-3">
                      <StatusPill value={link.usedAt ? `Applied ${localDate(link.usedAt)}` : "Applied"} fallback="success" />
                      {link.applicationId && <span className="text-sm text-blue-700">Open →</span>}
                    </span>
                  </>
                );
                return (
                  <li key={link.id}>
                    {link.applicationId ? (
                      <Link
                        to={`/applications/v2/${link.applicationId}`}
                        className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50"
                      >
                        {row}
                      </Link>
                    ) : (
                      <div className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm">{row}</div>
                    )}
                  </li>
                );
              }
              return (
                <li key={link.id} className={"space-y-2 p-3 " + (justCreated === link.id ? "bg-blue-50" : "")}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-medium text-slate-800">{link.label || "Pre-priced link"}</span>
                    <StatusPill value={status.value} fallback={status.tone} />
                  </div>
                  <p className="text-sm text-slate-600">{summary(link)}</p>
                  <p className="text-xs text-muted-foreground">
                    For one merchant · {open ? "works until" : link.state === "Expired" ? "expired" : "created"}{" "}
                    {localDate(open || link.state === "Expired" ? link.expiresAt : link.createdAt)}
                  </p>
                  {open && (
                    <Input
                      readOnly
                      value={url(link.path)}
                      onFocus={(e) => e.currentTarget.select()}
                      className="bg-muted font-mono text-xs text-slate-700"
                      aria-label="Pre-priced link"
                    />
                  )}
                  <div className="flex flex-wrap gap-2">
                    {open && (
                      <>
                        <Button type="button" size="sm" onClick={() => copy(link)}>
                          <Copy />
                          Copy link
                        </Button>
                        <Button type="button" size="sm" variant="outline" onClick={() => setEmailing(emailing === link.id ? null : link.id)}>
                          <Mail />
                          Email to merchant
                        </Button>
                        <Button type="button" size="sm" variant="outline" onClick={() => startEdit(link)} disabled={!!editing}>
                          Edit pricing
                        </Button>
                        <Button type="button" size="sm" variant="ghost" className="text-red-700" onClick={() => cancel(link)} disabled={cancelling === link.id || editing?.link?.id === link.id}>
                          Cancel link
                        </Button>
                      </>
                    )}
                    {!open && (
                      <Button type="button" size="sm" variant="outline" onClick={() => startEdit(null, link)} disabled={!!editing}>
                        New link with same pricing
                      </Button>
                    )}
                  </div>
                  {open && emailing === link.id && (
                    <form
                      className="grid gap-2 sm:grid-cols-[1fr_1fr_auto]"
                      onSubmit={(e) => { e.preventDefault(); void sendEmail(link); }}
                    >
                      <Input
                        type="email"
                        value={emailTo}
                        placeholder="merchant@business.com"
                        aria-label="Merchant email"
                        onChange={(e) => setEmailTo(e.target.value)}
                      />
                      <Input
                        value={emailName}
                        placeholder="Name (optional)"
                        aria-label="Merchant name"
                        onChange={(e) => setEmailName(e.target.value)}
                      />
                      <Button type="submit" size="sm" disabled={sending}>
                        {sending ? "Sending…" : "Send"}
                      </Button>
                    </form>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
